import {
  MecImage,
  PDP_SWATCH_NO_COLOUR,
  ProductImageType,
  isPrimaryProductImage,
} from '@adapters/bigcommerce/types/product';
import { normalizeImageVariantPreference } from '@utils/imageVariantPreference';
import { isLfsProductScopedSwatch } from '@utils/lfsProductScope';

/** Max primary (hero) images in the ordered PDP gallery strip. */
export const MAX_PDP_GALLERY_PRI = 1;

/** Max alternate angle images after PRI. */
export const MAX_PDP_GALLERY_ALT = 16;

/** Max lifestyle images after PRI and ALT. */
export const MAX_PDP_GALLERY_LFS = 2;

/** Max images returned by ordering (PRI + ALT + LFS caps). */
export const MAX_PDP_GALLERY_ORDERED =
  MAX_PDP_GALLERY_PRI + MAX_PDP_GALLERY_ALT + MAX_PDP_GALLERY_LFS;

const getSortOrder = (image: MecImage): number =>
  typeof image.sort_order === 'number' && !Number.isNaN(image.sort_order)
    ? image.sort_order
    : 0;

const compareBySortOrder = (a: MecImage, b: MecImage): number =>
  getSortOrder(a) - getSortOrder(b);

const swatchGroupKey = (img: MecImage): string => img.swatchMatch ?? '';

/**
 * Returns typed ALT images from the swatch with the most ALTs, excluding `priAltColourFilter`.
 * Used to fill ALT slots when the active swatch has no ALTs of its own, or has no PRI and at
 * most 1 ALT. Ties break by lexicographic `swatchMatch`.
 */
function altSortedFromRichestOtherSwatch(
  list: MecImage[],
  priAltColourFilter: string
): MecImage[] {
  const altCandidates = list.filter(
    (img) =>
      img.imageType !== ProductImageType.LFS &&
      img.imageType === ProductImageType.ALT &&
      !isPrimaryProductImage(img)
  );

  const bySwatch = new Map<string, MecImage[]>();
  for (const img of altCandidates) {
    const key = swatchGroupKey(img);
    const bucket = bySwatch.get(key);
    if (bucket) bucket.push(img);
    else bySwatch.set(key, [img]);
  }

  const others = [...bySwatch.entries()].filter(
    ([key]) => key !== priAltColourFilter
  );
  if (others.length === 0) return [];

  const maxCount = Math.max(...others.map(([, imgs]) => imgs.length));
  const tied = others.filter(([, imgs]) => imgs.length === maxCount);
  tied.sort((a, b) => a[0].localeCompare(b[0]));
  const [, best] = tied[0];

  return best.sort(compareBySortOrder).slice(0, MAX_PDP_GALLERY_ALT);
}

/**
 * When the PDP colour filter is {@link PDP_SWATCH_NO_COLOUR}, do not treat that sentinel as an
 * LFS colour key — only product-scoped LFS rows are colour-eligible (see
 * {@link isLfsProductScopedSwatch}). PRI/ALT still use the raw filter name.
 */
function lfsSwatchFilterFor(colourFilterName: string | null): string | null {
  if (colourFilterName == null) return null;
  if (colourFilterName === PDP_SWATCH_NO_COLOUR) return null;
  return colourFilterName;
}

function buildOrderedFromList(
  list: MecImage[],
  priAltColourFilter: string | null,
  lfsColourSwatchFilter: string | null
): MecImage[] {
  const colourMatched = list.filter((img) => {
    if (img.imageType === ProductImageType.LFS) return false;
    if (priAltColourFilter) return img.swatchMatch === priAltColourFilter;
    return true;
  });

  const primarySorted = colourMatched
    .filter((img) => isPrimaryProductImage(img))
    .sort(compareBySortOrder)
    .slice(0, MAX_PDP_GALLERY_PRI);
  let altSorted = colourMatched
    .filter(
      (img) =>
        img.imageType === ProductImageType.ALT && !isPrimaryProductImage(img)
    )
    .sort(compareBySortOrder)
    .slice(0, MAX_PDP_GALLERY_ALT);

  // Borrow typed ALTs from the richest other swatch when:
  // - the active swatch has no typed ALTs (no images at all, LFS-only, or untyped-only rows), OR
  // - the active swatch has no PRI and at most 1 ALT (not enough for a meaningful gallery —
  //   the single ALT acts as the de-facto hero; borrow more angles to fill remaining slots).
  const shouldBorrow =
    priAltColourFilter !== null &&
    (altSorted.length === 0 ||
      (primarySorted.length === 0 && altSorted.length <= 1));

  if (shouldBorrow) {
    const borrowed = altSortedFromRichestOtherSwatch(list, priAltColourFilter);
    if (borrowed.length > 0) {
      altSorted = [...altSorted, ...borrowed].slice(0, MAX_PDP_GALLERY_ALT);
    }
  }

  if (
    priAltColourFilter &&
    primarySorted.length === 0 &&
    altSorted.length === 0
  ) {
    return [];
  }

  const allLfs = list.filter((img) => img.imageType === ProductImageType.LFS);
  const lfsEligible = allLfs.filter((img) => {
    const { swatchMatch } = img;
    if (lfsColourSwatchFilter) {
      return (
        swatchMatch === lfsColourSwatchFilter ||
        isLfsProductScopedSwatch(swatchMatch)
      );
    }
    return isLfsProductScopedSwatch(swatchMatch);
  });
  const lfsSorted = lfsEligible
    .sort(compareBySortOrder)
    .slice(0, MAX_PDP_GALLERY_LFS);

  return [...primarySorted, ...altSorted, ...lfsSorted];
}

/**
 * PDP ImageGallery order for **typed** colour imagery: up to {@link MAX_PDP_GALLERY_PRI}
 * primary (including legacy `primary`), up to {@link MAX_PDP_GALLERY_ALT} `ALT`, up to
 * {@link MAX_PDP_GALLERY_LFS} `LFS`. Untyped non-LFS rows (neither primary nor `ALT`) are
 * omitted. No `targetRatio` filtering.
 *
 * **LFS:** For any PDP colour, eligible LFS rows are **that colour’s LFS** or **product-scoped**
 * LFS (empty / `NO_COLOUR` swatch segment — same as `|LFS`), merged and sorted by `sort_order`,
 * then capped at {@link MAX_PDP_GALLERY_LFS}. Product-scoped LFS therefore appears for every
 * colour variant whenever PRI/ALT exist for the active swatch. When there is no LFS colour key
 * (no PDP colour, or variant `NO_COLOUR`; see {@link lfsSwatchFilterFor}), only product-scoped
 * LFS are eligible. Other-colour LFS is never used.
 *
 * **Colour filter / no images:** If `colourFilterName` is set and the active swatch has no
 * images at all, the borrow logic still runs — ALTs from the richest other swatch are returned
 * so the gallery can show them. `ImageGallery` prepends a placeholder tile in this case so the
 * user knows the images are not colour-specific. Returns `[]` (shows standalone placeholder)
 * only when no typed ALTs exist on any other swatch either.
 *
 * **ALT borrow:** ALTs are borrowed from the swatch with the most typed ALTs (lexicographic
 * tie-break) when the active swatch has no typed ALTs, OR when it has no PRI and at most 1
 * ALT (de-facto hero with no additional angles). Borrow supplements any existing ALTs rather
 * than replacing them. PRI and LFS stay scoped to the active swatch. Untyped rows (missing or
 * unrecognised `imageType`) are omitted and do not prevent borrowing.
 *
 * Primary detection uses {@link isPrimaryProductImage}.
 */
export function buildPdpImageGalleryOrder(
  images: MecImage[] | undefined,
  colourFilterName: string | null
): MecImage[] {
  const list = normalizeImageVariantPreference(images ?? [], {
    preferTransparent: true,
  });

  // No early return for colourImages.length === 0: buildOrderedFromList will borrow ALTs from
  // other swatches so the gallery can show them. ImageGallery prepends a placeholder tile
  // when the active swatch has no images of its own (detected via colourHasNoImages).

  return buildOrderedFromList(
    list,
    colourFilterName,
    lfsSwatchFilterFor(colourFilterName)
  );
}
